import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate, useParams } from "react-router-dom";
import { createCoupon, getSingleCoupon, updateCoupon } from "../Services/couponService";
import { toast } from "react-toastify";

export default function CouponForm({ mode = "add" }) {
    const navigate = useNavigate();
    const { id } = useParams();
    const isAddMode = mode === "add";
    const isEditMode = mode === "edit";
    const isViewMode = mode === "view";

    const [couponData, setCouponData] = useState({
        code: "",
        description: "",
        type: "fixed",
        value: "",
        minOrderValue: "",
        maxUsage: "",
        startDate: null,
        expiryDate: null,
    });

    useEffect(() => {
        const fetchCouponDetails = async () => {
            if (isEditMode && id) {
                try {
                    const res = await getSingleCoupon(id);
                    const data = res.data?.data?.promo;
                    setCouponData({
                        code: data.code,
                        description: data.description,
                        type: data.type,
                        value: data.discount,
                        minOrderValue: data.minOrderValue,
                        maxUsage: data.noOfUsage,
                        startDate: data.fromDate ? new Date(data.fromDate) : null,
                        expiryDate: data.toDate ? new Date(data.toDate) : null,

                    });
                } catch (error) {
                    console.error("Error fetching coupon:", error);
                    toast.error("Failed to load coupon data");
                }
            }
        };
        fetchCouponDetails();
    }, [isEditMode, id]);

    const isFormValid = () => {
        return (
            (couponData.code || "").trim() !== "" &&
            (couponData.description || "").trim() !== "" &&
            Number(couponData.value) > 0 &&
            Number(couponData.minOrderValue) > 0 &&
            Number(couponData.maxUsage) > 0 &&
            couponData.startDate !== null &&
            couponData.expiryDate !== null
        );
    };

    const handleInputChange = (key, value) => {
        if (key === "value") {
            const numericValue = Number(value);

            if (couponData.type === "percentage" && numericValue > 100) {
                toast.warn("Percentage discount can't exceed 100%");
                return;
            }
        }

        setCouponData({ ...couponData, [key]: value });
    };

    const saveCoupon = async () => {
        const payload = {
            code: couponData.code,
            description: couponData.description,
            type: couponData.type,
            discount: Number(couponData.value),
            minOrderValue: Number(couponData.minOrderValue),
            noOfUsage: Number(couponData.maxUsage),
            fromDate: couponData.startDate?.toISOString(),
            toDate: couponData.expiryDate?.toISOString(),
        };

        try {
            let response;

            if (isEditMode && id) {
                response = await updateCoupon(id, payload);
                if (response?.data?.status === true) {
                    toast.success(response?.data?.data?.message || "Coupon Updated Successfully");
                    navigate("/coupon-management");
                } else {
                    toast.error(response?.data?.data?.message || "Failed to update coupon");
                }
            } else {
                response = await createCoupon(payload);
                if (response?.data?.status === true) {
                    toast.success(response?.data?.data?.message || "Coupon Created Successfully");
                    navigate("/coupon-management");
                } else {
                    toast.error(response?.data?.data?.message || "Failed to create coupon");
                }
            }

        } catch (err) {
            const errorMessage =
                err?.response?.data?.data?.message ||
                err?.response?.data?.message ||
                "Something went wrong while saving the coupon";

            toast.error(errorMessage);
        }
    };

    const cancelCoupon = () => {
        setCouponData({
            code: "",
            description: "",
            type: "fixed",
            value: "",
            minOrderValue: "",
            maxUsage: "",
            startDate: null,
            expiryDate: null,
        });

        navigate("/coupon-management");
    };

    return (
        <div className="coupon-container">
            <h2>{isAddMode ? "Add Coupon" : isEditMode ? "Edit Coupon" : "View Coupon"}</h2>

            <div className="form-grid">
                <div className="form-row">
                    <label>Coupon Code</label>
                    <input
                        type="text"
                        placeholder="Enter Coupon Code"
                        value={couponData.code}
                        readOnly={isViewMode}
                        onChange={(e) => handleInputChange("code", e.target.value)}
                    />
                </div>

                <div className="form-row">
                    <label>Description</label>
                    <input
                        type="text"
                        placeholder="Enter Description"
                        value={couponData.description}
                        readOnly={isViewMode}
                        onChange={(e) => handleInputChange("description", e.target.value)}
                    />
                </div>


                <div className="form-row">
                    <label>Discount Type</label>
                    <div className="segment-buttons">
                        {["fixed", "percentage"].map((type) => (
                            <button
                                key={type}
                                className={couponData.type === type ? "active" : ""}
                                onClick={() => !isViewMode && handleInputChange("type", type)}
                                disabled={isViewMode}
                            >
                                {type.toUpperCase()}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="form-row">
                    <label>Value</label>
                    <input
                        type="number"
                        placeholder="Discount Value"
                        value={couponData.value}
                        onChange={(e) => handleInputChange("value", e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        readOnly={isViewMode}
                        className="no-spinner"
                    />

                </div>

                <div className="form-row">
                    <label>Minimum Order Value</label>
                    <input
                        type="number"
                        placeholder="Min Order Value"
                        value={couponData.minOrderValue}
                        readOnly={isViewMode}
                        className="no-spinner"
                        onWheel={(e) => e.target.blur()}
                        onChange={(e) => handleInputChange("minOrderValue", e.target.value)}
                    />
                </div>

                <div className="form-row">
                    <label>Max Usage</label>
                    <input
                        type="number"
                        placeholder="Maximum Usages"
                        value={couponData.maxUsage}
                        className="no-spinner"
                        onWheel={(e) => e.target.blur()}
                        readOnly={isViewMode}
                        onChange={(e) => handleInputChange("maxUsage", e.target.value)}
                    />
                </div>
                <div className="form-row">
                    <label>Start Date</label>
                    <div className="date-range-picker">
                        <DatePicker
                            selected={couponData.startDate instanceof Date && !isNaN(couponData.startDate) ? couponData.startDate : null}
                            onChange={(date) => handleInputChange("startDate", date)}
                            selectsStart
                            startDate={couponData.startDate}
                            endDate={couponData.expiryDate}
                            minDate={new Date()}
                            placeholderText="Start Date"
                            disabled={isViewMode}
                        />

                    </div>
                </div>

                <div className="form-row">
                    <label>Expiry Date</label>
                    <div className="date-range-picker">
                        <DatePicker
                            selected={couponData.expiryDate instanceof Date && !isNaN(couponData.expiryDate) ? couponData.expiryDate : null}
                            onChange={(date) => handleInputChange("expiryDate", date)}
                            selectsEnd
                            startDate={couponData.startDate}
                            endDate={couponData.expiryDate}
                            minDate={couponData.startDate}
                            placeholderText="End Date"
                            disabled={isViewMode}
                        />
                    </div>
                </div>

            </div>

            <div className="form-actions">
                <button onClick={cancelCoupon}>Cancel</button>
                {(isAddMode || isEditMode) && (
                    <button onClick={saveCoupon} disabled={!isFormValid()}>
                        Save Changes
                    </button>
                )}
            </div>
        </div>
    );
}
